import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { effectiveSubscriptionEnd } from "@/lib/subscription-time";
import {
  cacheProducts, catalogKey, clearOfflineWorkspace, offlineIdentity, validOfflineIdentity,
  workspaceRecord, OFFLINE_WORKSPACE_EVENT, type OfflineIdentity, type OfflineCatalog,
} from "@/lib/offline-workspace";
import { listOfflineSales } from "@/lib/offline-sales";
import { offlineRegistration } from "@/lib/offline-client";

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
  businessType?: string | null;
  permissions: string[];
}

export interface AuthStore {
  id: number;
  name: string;
  industryType?: string | null;
  businessType?: string | null;
  plan: string;
  subscriptionStatus: string;
  trialEndsAt: string;
  isActive: boolean;
  address?: string | null;
  phone?: string | null;
  logoUrl?: string | null;
  receiptFooter?: string | null;
  receiptPrefix?: string | null;
  barcodePrefix?: string | null;
  referralCode?: string | null;
  loyaltyEarnRate?: number | null;
  taxRate?: number | null;
}

interface AuthState {
  user: AuthUser | null;
  store: AuthStore | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasActiveSubscription: boolean;
  trialDaysLeft: number;
  refetch: () => void;
  logout: () => Promise<boolean>;
  offlineEnabled: boolean;
  offlineRestored: boolean;
  offlineExpiresAt: number | null;
  prepareOffline: (branchId: number | null) => Promise<void>;
  disableOffline: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  store: null,
  isLoading: true,
  isAuthenticated: false,
  hasActiveSubscription: false,
  trialDaysLeft: 0,
  refetch: () => {},
  logout: async () => false,
  offlineEnabled: false,
  offlineRestored: false,
  offlineExpiresAt: null,
  prepareOffline: async () => {},
  disableOffline: async () => {},
  hasPermission: () => true,
});

export function subscriptionAllowsAccess(
  store: Pick<AuthStore, "subscriptionStatus" | "trialEndsAt"> | null,
) {
  if (!store) return false;
  return (store.subscriptionStatus === "trial" || store.subscriptionStatus === "active")
    && effectiveSubscriptionEnd(store.trialEndsAt) > new Date();
}

function computeSubscriptionState(store: AuthStore | null) {
  if (!store) return { hasActiveSubscription: false, trialDaysLeft: 0 };
  const now = new Date();
  const trialEndsAt = effectiveSubscriptionEnd(store.trialEndsAt);
  const trialActive = store.subscriptionStatus === "trial" && trialEndsAt > now;
  const trialDaysLeft = trialActive
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  return { hasActiveSubscription: subscriptionAllowsAccess(store), trialDaysLeft };
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [store, setStore] = useState<AuthStore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [offlineRestored, setOfflineRestored] = useState(false);
  const [offlineExpiresAt, setOfflineExpiresAt] = useState<number | null>(null);

  const restoreOffline = async (): Promise<boolean> => {
    try {
      const saved = await workspaceRecord<OfflineIdentity>("identity");
      if (!validOfflineIdentity(saved)) return false;
      setUser(saved.user);
      setStore(saved.store);
      setOfflineExpiresAt(saved.expiresAt);
      setOfflineEnabled(true);
      setOfflineRestored(true);
      return true;
    } catch { return false; }
  };

  const fetchMe = async (background = false) => {
    const retryDeadline = Date.now() + (background ? 0 : 120_000);
    let retryDelay = 1_000;
    if (!background) setIsLoading(true);

    try {
      if (!navigator.onLine) {
        if (await restoreOffline()) return;
        setUser(null);
        setStore(null);
        return;
      }
      while (true) {
        try {
          const res = await fetch(`${BASE}/api/auth/me`, {
            credentials: "include",
            cache: "no-store",
          });
          if (res.ok) {
            const data = await res.json();
            const previous = await workspaceRecord<OfflineIdentity>("identity").catch(() => undefined);
            if (previous && previous.user.id === data.user.id && previous.store.id === data.store.id) {
              const fresh = offlineIdentity(data.user, data.store);
              if (validOfflineIdentity(fresh)) {
                try {
                  await workspaceRecord("identity", fresh);
                  setOfflineEnabled(true);
                  setOfflineExpiresAt(fresh.expiresAt);
                } catch {
                  await clearOfflineWorkspace().catch(() => {});
                  setOfflineEnabled(false);
                  setOfflineExpiresAt(null);
                }
              } else {
                await clearOfflineWorkspace().catch(() => {});
                setOfflineEnabled(false);
                setOfflineExpiresAt(null);
              }
            } else {
              // Never expose a prior user's device-local catalogue to a new account.
              if (previous) await clearOfflineWorkspace().catch(() => {});
              setOfflineEnabled(false);
              setOfflineExpiresAt(null);
            }
            setUser(data.user);
            setStore(data.store);
            setOfflineRestored(false);
            return;
          }
          if (res.status === 401) {
            await clearOfflineWorkspace().catch(() => {});
            setUser(null);
            setStore(null);
            setOfflineEnabled(false);
            setOfflineRestored(false);
            return;
          }
          if (res.status < 500) {
            await clearOfflineWorkspace().catch(() => {});
            setUser(null);
            setStore(null);
            setOfflineEnabled(false);
            setOfflineRestored(false);
            return;
          }
          if (await restoreOffline()) return;
          if (Date.now() >= retryDeadline) {
            setUser(null);
            setStore(null);
            return;
          }

          const retryAfterSeconds = Number(res.headers.get("Retry-After"));
          const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
            ? Math.min(30_000, retryAfterSeconds * 1_000)
            : retryDelay;
          await new Promise(resolve => window.setTimeout(resolve, waitMs));
          retryDelay = Math.min(retryDelay * 2, 10_000);
        } catch {
          if (await restoreOffline()) return;
          if (Date.now() >= retryDeadline) {
            setUser(null);
            setStore(null);
            return;
          }
          await new Promise(resolve => window.setTimeout(resolve, retryDelay));
          retryDelay = Math.min(retryDelay * 2, 10_000);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchMe(); }, []);

  useEffect(() => {
    const onOnline = () => { void fetchMe(true); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  useEffect(() => {
    if (!offlineRestored) return;
    const timer = window.setInterval(() => {
      if (navigator.onLine) void fetchMe(true);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [offlineRestored]);

  useEffect(() => {
    if (!offlineEnabled || !offlineExpiresAt) return;
    const timer = window.setInterval(() => {
      if (Date.now() < offlineExpiresAt || (navigator.onLine && !offlineRestored)) return;
      setUser(null);
      setStore(null);
      setOfflineRestored(false);
      setOfflineEnabled(false);
      void clearOfflineWorkspace().catch(() => {});
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [offlineEnabled, offlineRestored, offlineExpiresAt]);

  const prepareOffline = async (branchId: number | null) => {
    if (!navigator.onLine || !navigator.serviceWorker?.controller || !offlineRegistration()?.active) {
      throw new Error("Open the published POS online and wait for offline app files to finish preparing.");
    }
    const persisted = await navigator.storage?.persist?.().catch(() => false);
    const meResponse = await fetch(`${BASE}/api/auth/me`, { credentials: "include", cache: "no-store" });
    if (!meResponse.ok) throw new Error("Sign in online before preparing offline access.");
    const account = await meResponse.json();
    if (account.user?.id !== user?.id || account.store?.id !== store?.id) {
      throw new Error("Your sign-in changed. Reload the app before preparing offline access.");
    }
    const identity = offlineIdentity(account.user, account.store);
    if (!validOfflineIdentity(identity)) throw new Error("Offline access is unavailable for this account or subscription.");
    const [productsResponse, customersResponse, staffResponse, branchesResponse] = await Promise.all([
      fetch(`${BASE}/api/products${branchId == null ? "" : `?branchId=${branchId}`}`, { credentials: "include", cache: "no-store" }),
      fetch(`${BASE}/api/customers`, { credentials: "include", cache: "no-store" }),
      fetch(`${BASE}/api/staff`, { credentials: "include", cache: "no-store" }),
      fetch(`${BASE}/api/branches`, { credentials: "include", cache: "no-store" }),
    ]);
    if (![productsResponse, customersResponse, staffResponse, branchesResponse].every(response => response.ok)) {
      throw new Error("Could not download the full POS catalogue. Reconnect and try again.");
    }
    const [products, customers, staff, branches] = await Promise.all([
      productsResponse.json(), customersResponse.json(), staffResponse.json(), branchesResponse.json(),
    ]);
    if (!Array.isArray(products) || !Array.isArray(customers) || !Array.isArray(staff) || !Array.isArray(branches)) {
      throw new Error("The POS catalogue response was incomplete. Nothing was prepared.");
    }
    if (branchId !== null && !branches.some(branch => branch.id === branchId && branch.isActive)) {
      throw new Error("The selected branch is no longer available.");
    }
    const catalog: OfflineCatalog = {
      savedAt: Date.now(),
      products: cacheProducts(products),
      customers: customers.map(({ id, name, discountRate, groupDiscountValue, groupDiscountType }: OfflineCatalog["customers"][number]) =>
        ({ id, name, discountRate, groupDiscountValue, groupDiscountType })),
      staff: staff.map(({ id, name, isActive }: OfflineCatalog["staff"][number]) => ({ id, name, isActive })),
    };
    try {
      await workspaceRecord(catalogKey(identity.store.id, identity.user.id, branchId), catalog);
      await workspaceRecord(`branches:${identity.store.id}:${identity.user.id}`, branches);
      await workspaceRecord("identity", identity);
    } catch (error) {
      await clearOfflineWorkspace().catch(() => {});
      throw error;
    }
    setOfflineEnabled(true);
    setOfflineExpiresAt(identity.expiresAt);
    window.dispatchEvent(new Event(OFFLINE_WORKSPACE_EVENT));
    if (persisted === false) console.warn("Persistent device storage was not granted; unsynced sales remain vulnerable to browser eviction.");
  };

  const disableOffline = async () => {
    await clearOfflineWorkspace();
    setOfflineEnabled(false);
    setOfflineRestored(false);
    setOfflineExpiresAt(null);
  };

  const logout = async (): Promise<boolean> => {
    if (!navigator.onLine) {
      window.alert("Reconnect before signing out so the server session can be ended.");
      return false;
    }
    if (store && user) {
      const unsynced = await listOfflineSales(store.id, user.id);
      if (unsynced.length && !window.confirm(`${unsynced.length} sale(s) are still saved on this device. Signing out will pause syncing until this same account signs back in on this device. Continue?`)) return false;
    }
    const response = await fetch(`${BASE}/api/auth/logout`, { method: "POST", credentials: "include" });
    if (!response.ok) throw new Error("Could not sign out. Reconnect and try again.");
    try {
      await disableOffline();
    } catch {
      setUser(null);
      setStore(null);
      throw new Error("Signed out online, but this device's offline access could not be erased. Clear its browser site data before sharing it.");
    }
    setUser(null);
    setStore(null);
    return true;
  };

  const { hasActiveSubscription, trialDaysLeft } = computeSubscriptionState(store);

  const hasPermission = (perm: string) => {
    if (!user) return false;
    if (user.role === "admin" || user.role === "superadmin") return true;
    return user.permissions?.includes(perm) ?? false;
  };

  return (
    <AuthContext.Provider value={{
      user,
      store,
      isLoading,
      isAuthenticated: !!user,
      hasActiveSubscription,
      trialDaysLeft,
      refetch: fetchMe,
      logout,
      offlineEnabled,
      offlineRestored,
      offlineExpiresAt,
      prepareOffline,
      disableOffline,
      hasPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
